import { useState, useEffect, useCallback } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { PomodoroProvider } from "@/contexts/PomodoroContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { BottomNavigation } from "@/components/BottomNavigation";
import { useReferralActivityTracker } from "@/hooks/useReferralActivityTracker";
import { useReferralNotifications } from "@/hooks/useReferralNotifications";
import { useCloudSync } from "@/hooks/useCloudSync";
import { useAuth } from "@/hooks/useAuth";
import { useAppSettings } from "@/hooks/useAppSettings";
import { AuthProvider, useAuthContext } from "@/providers/AuthProvider";
import { CloudRestoreDialog } from "@/components/profile/CloudRestoreDialog";
import { OnboardingSlideshow } from "@/components/OnboardingSlideshow";
import { LegalActivationModal } from "@/components/legal/LegalActivationModal";
import { AnalyticsHead } from "@/components/AnalyticsHead";
import { AppHeader } from "@/components/AppHeader";
import { PinLockScreen } from "@/components/PinLockScreen";
import { useLocalAuth } from "@/hooks/useLocalAuth";

// Импорт страниц
import Dashboard from "./pages/Dashboard";
import Habits from "./pages/Habits";
import Tasks from "./pages/Tasks";
import Finance from "./pages/Finance";
import Services from "./pages/Services";
import Ideas from "./pages/Ideas";
import Statistics from "./pages/Statistics";
import Profile from "./pages/Profile";
import ProfileSettings from "./pages/ProfileSettings";
import PublicProfilePreview from "./pages/PublicProfilePreview";
import UserCatalog from "./pages/UserCatalog";
import Achievements from "./pages/Achievements";
import PartnerProgram from "./pages/PartnerProgram";
import AboutApp from "./pages/AboutApp";
import Infocenter from "./pages/Infocenter";
import Auth from "./pages/Auth";
import Upgrade from "./pages/Upgrade";
import Admin from "./pages/Admin";
import AdminUserDetail from "./pages/AdminUserDetail";
import TagPage from "./pages/TagPage";
import Rating from "./pages/Rating";
import RewardsShop from "./pages/RewardsShop";
import StarHistory from "./pages/StarHistory";
import Archive from "./pages/Archive";
import Focus from "./pages/Focus";
import UserProfile from "./pages/UserProfile";
import Notifications from "./pages/Notifications";
import Goals from "./pages/Goals";
import GoalDetail from "./pages/GoalDetail";
import LifeFocus from "./pages/LifeFocus";
import SphereDetail from "./pages/SphereDetail";
import NotFound from "./pages/NotFound";
import DayPlan from "./pages/DayPlan";
import DaySummary from "./pages/DaySummary";
import TeamPlan from "./pages/TeamPlan";
import Team from "./pages/Team";
import TeamPricing from "./pages/TeamPricing";
import TeamFocus from "./pages/TeamFocus";
import TeamSphereDetail from "./pages/TeamSphereDetail";
import TeamWorkspace from "./pages/TeamWorkspace";
import TeamProject from "./pages/TeamProject";
import TeamSprint from "./pages/TeamSprint";
import TeamPublicProfilePage from "./pages/TeamPublicProfilePage";
import TeamMember from "./pages/TeamMember";
import SuccessDiary from "./pages/SuccessDiary";
import Reflection from "./pages/Reflection";

/**
 * Конфигурация QueryClient с поддержкой Offline Mode
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 минут актуальности
      gcTime: 1000 * 60 * 60 * 24, // 24 часа хранения в памяти
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});


const AppErrorFallback = ({ error }: { error: Error }) => (
  <div className="h-screen flex flex-col items-center justify-center p-6 text-center bg-background">
    <h2 className="text-xl font-bold text-red-500 mb-2">Ошибка инициализации</h2>
    <p className="text-sm text-muted-foreground mb-4">
      Не удалось запустить приложение.
    </p>
    <pre className="text-[10px] bg-muted p-2 rounded mb-4 max-w-full overflow-auto text-left">
      {error.message}
    </pre>
    <button 
      onClick={() => window.location.href = '/'}
      className="px-6 py-2 bg-primary text-white rounded-lg font-medium"
    >
      Перезагрузить приложение
    </button>
  </div>
);

const CloudSyncProvider = ({ children }: { children: React.ReactNode }) => {
  const { triggerSync } = useCloudSync();

  useEffect(() => {
    const handleStorageChange = () => {
      triggerSync();
    };
    window.addEventListener('habitflow-data-changed', handleStorageChange);
    return () => {
      window.removeEventListener('habitflow-data-changed', handleStorageChange);
    };
  }, [triggerSync]);

  return <>{children}</>;
};

const SubscriptionWrapper = ({ children }: { children: React.ReactNode }) => {
  const { user, profile } = useAuth();
  return (
    <SubscriptionProvider user={user} referralCode={profile?.referral_code || null}>
      {children}
    </SubscriptionProvider>
  );
};

const AccessControlWrapper = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { accessControl, loading: settingsLoading } = useAppSettings();
  const location = useLocation();
  
  if (authLoading || settingsLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  const publicRoutes = ['/auth', '/admin'];
  if (publicRoutes.includes(location.pathname)) {
    return <>{children}</>;
  }
  
  if (!accessControl.guest_access_enabled && !user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
};

const ONBOARDING_STORAGE_KEY = 'topfocus_onboarding_seen';

const AppContent = () => {
  const [habitDialogOpen, setHabitDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);

  const { user, profile, loading: authLoading, refetchProfile } = useAuthContext();
  const { needsLocalAuth, markVerified } = useLocalAuth();
  const location = useLocation();

  // Local auth (PIN/biometric) gate
  const [localAuthPassed, setLocalAuthPassed] = useState(false);

  useEffect(() => {
    // If no local auth configured or already verified, skip
    if (!needsLocalAuth()) {
      setLocalAuthPassed(true);
    }
  }, [needsLocalAuth]);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Legal firewall state
  const [showLegal, setShowLegal] = useState(false);

  useReferralActivityTracker();
  useReferralNotifications();

  // Check onboarding & legal status after auth loads
  useEffect(() => {
    if (authLoading || !user) return;

    const onboardingSeen = localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
    if (!onboardingSeen) {
      setShowOnboarding(true);
      return;
    }

    // If onboarding done, check legal consents
    if (profile && !profile.legal_consents_accepted) {
      setShowLegal(true);
    }
  }, [authLoading, user, profile]);

  const handleOnboardingClose = useCallback(() => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setShowOnboarding(false);
    // After onboarding, show legal if not accepted
    if (user && profile && !profile.legal_consents_accepted) {
      setShowLegal(true);
    }
  }, [user, profile]);

  const handleLegalAccepted = useCallback(async () => {
    setShowLegal(false);
    await refetchProfile();
  }, [refetchProfile]);

  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const hideHeader = location.pathname === '/auth';

  // Show PIN lock screen if local auth is required and not yet passed
  if (user && !localAuthPassed && location.pathname !== '/auth') {
    return (
      <PinLockScreen onVerified={() => {
        markVerified();
        setLocalAuthPassed(true);
      }} />
    );
  }

  return (
    <CloudSyncProvider>
      <AnalyticsHead />
      <AccessControlWrapper>
        <OnboardingSlideshow open={showOnboarding} onClose={handleOnboardingClose} />
        {user && showLegal && (
          <LegalActivationModal
            open={showLegal}
            userId={user.id}
            onAccepted={handleLegalAccepted}
          />
        )}
        <CloudRestoreDialog />
        {!hideHeader && <AppHeader />}
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/habits" element={<Habits openDialog={habitDialogOpen} onDialogClose={() => setHabitDialogOpen(false)} />} />
          <Route path="/tasks" element={<Tasks openDialog={taskDialogOpen} onDialogClose={() => setTaskDialogOpen(false)} />} />
          <Route path="/finance" element={<Finance openDialog={transactionDialogOpen} onDialogClose={() => setTransactionDialogOpen(false)} />} />
          <Route path="/services" element={<Services />} />
          <Route path="/ideas" element={<Ideas />} />
          <Route path="/statistics" element={<Statistics />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/settings" element={<ProfileSettings />} />
          <Route path="/profile/public" element={<PublicProfilePreview />} />
          <Route path="/users" element={<UserCatalog />} />
          <Route path="/achievements" element={<Achievements />} />
          <Route path="/partner-program" element={<PartnerProgram />} />
          <Route path="/about" element={<AboutApp />} />
          <Route path="/news" element={<Navigate to="/infocenter?tab=news" replace />} />
          <Route path="/infocenter" element={<Infocenter />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/upgrade" element={<Upgrade />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
          <Route path="/tag/:tagId" element={<TagPage />} />
          <Route path="/rating" element={<Rating />} />
          <Route path="/rewards" element={<RewardsShop />} />
          <Route path="/star-history" element={<StarHistory />} />
          <Route path="/archive" element={<Archive />} />
          <Route path="/focus" element={<Focus />} />
          <Route path="/user/:userId" element={<UserProfile />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/goals" element={<Goals />} />
          <Route path="/goals/:id" element={<GoalDetail />} />
          <Route path="/life-focus" element={<LifeFocus />} />
          <Route path="/sphere/:sphereKey" element={<SphereDetail />} />
          <Route path="/day-plan" element={<DayPlan />} />
          <Route path="/day-summary" element={<DaySummary />} />
          <Route path="/team" element={<Team />} />
          <Route path="/team-plan" element={<TeamPlan />} />
          <Route path="/pricing/team" element={<TeamPricing />} />
           <Route path="/success-diary" element={<SuccessDiary />} />
          <Route path="/reflection" element={<Reflection />} />
          <Route path="/team-focus" element={<TeamFocus />} />
          <Route path="/help" element={<Navigate to="/infocenter?tab=help" replace />} />
          <Route path="/team/sphere/:sphereKey" element={<TeamSphereDetail />} />
          <Route path="/team/workspace/:workspaceId" element={<TeamWorkspace />} />
          <Route path="/team/project/:projectId" element={<TeamProject />} />
          <Route path="/team/sprint/:sprintId" element={<TeamSprint />} />
          <Route path="/team/profile/:teamId" element={<TeamPublicProfilePage />} />
          <Route path="/team/member/:memberId" element={<TeamMember />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <BottomNavigation
          onAddHabit={() => setHabitDialogOpen(true)}
          onAddTask={() => setTaskDialogOpen(true)}
          onAddTransaction={() => setTransactionDialogOpen(true)}
        />
      </AccessControlWrapper>
    </CloudSyncProvider>
  );
};

const App = () => (
  <ErrorBoundary FallbackComponent={AppErrorFallback}>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <PomodoroProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <SubscriptionWrapper>
                  <AppContent />
                </SubscriptionWrapper>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </PomodoroProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
